<template>
  <vs-wrapper>
    <vs-autocomplete
      data-test="user-selector-field"
      :disabled="disabled"
      :items="users"
      :label="title"
      :hint="description"
      :value="formData"
      :error-messages="errorMessages"
      @input="$emit('change', $event)"
    />
  </vs-wrapper>
</template>

<script>
import { mapGetters } from 'vuex'
import { sortBy, uniqBy } from 'lodash'

export default {
  name: 'UserSelectorField',
  props: {
    errorSchema: Object,
    formData: String,
    businessUnitId: String,
    params: Object,
    rootSchema: Object,
    schema: Object,
    title: String,
    description: String,
    uiSchema: Object,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      getVisibleUsers: 'users/getVisibleUsers',
      getUsersRelatedToBusinessUnit: 'users/getUsersRelatedToBusinessUnit',
      getUserBySid: 'users/getBySid',
      getBusinessUnitById: 'businessUnits/getBusinessUnitById',
    }),
    users() {
      // the form belongs to a single business unit, so only offer the users related to it
      const visibleUsers = this.businessUnitId
        ? this.getUsersRelatedToBusinessUnit(this.businessUnitId)
        : this.getVisibleUsers

      const baseUsers = Array.isArray(visibleUsers)
        ? visibleUsers
        : Object.values(visibleUsers || {})

      // a user related to the business unit through both its own scope and a partner
      // scope is returned twice, and would otherwise show up as two identical options
      const users = uniqBy(baseUsers, 'id').map((user) => {
        return {
          text: user.name,
          value: user.id,
          description: this.formatDescription(user),
        }
      })

      // a submission made before the options were narrowed can hold a user we no longer
      // offer - keep it in the list so the field still renders the name it was saved with
      if (this.selectedUserOutsideOptions) {
        users.push(this.selectedUserOutsideOptions)
      }

      return sortBy(users, ['text'])
    },
    selectedUserOutsideOptions() {
      if (!this.formData || !this.businessUnitId) {
        return null
      }

      const relatedUsers = this.getUsersRelatedToBusinessUnit(this.businessUnitId) ?? []
      if (relatedUsers.some((user) => user.id === this.formData)) {
        return null
      }

      // options carry the user sid, which is what a submission stores
      const selectedUser = this.getUserBySid(this.formData)
      if (!selectedUser) {
        return null
      }

      return {
        text: selectedUser.name,
        value: selectedUser.id,
        description: this.formatDescription(selectedUser),
      }
    },
    errorMessages() {
      const errors = this.errorSchema?.errors ?? []
      if (this.formData && typeof this.formData !== 'string') {
        errors.push('User value expected.')
      }
      return errors
    },
  },
  methods: {
    formatDescription(user) {
      const org = this.getBusinessUnitById(user.organizationId)
      const team = this.getBusinessUnitById(user.defaultBusinessUnitId)
      if (!org) {
        return team?.name ?? ''
      }
      if (!team) {
        return org.name
      } else {
        return org.name + ' > ' + team.name
      }
    },
  },
}
</script>

<style scoped></style>
